import { Stack, useRouter } from 'expo-router';
import { useDepositCashFlow } from 'features/moveMoney/providers/DepositCashFlowContextProvider';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';

interface BarcodeErrorScreenProps {
  onRetry?: () => void;
  messageHeadline?: string;
  messageBody?: string;
  hideRetryButton?: boolean;
}

export default function BarcodeErrorScreen({
  onRetry,
  messageHeadline,
  messageBody,
  hideRetryButton,
}: BarcodeErrorScreenProps) {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney');
  const { flowName } = useDepositCashFlow();

  useViewDidFocusScreenAnalyticsEvent(
    hideRetryButton
      ? undefined
      : {
          key: '/move-money/deposit-cash/barcode/loading-error',
          customAttributes: { flow_name: flowName },
        },
  );

  const { segmentClient } = useAnalyticsConfiguration();
  const hasLoggedActiveErrorRef = useRef(false);
  useEffect(() => {
    if (hideRetryButton && !hasLoggedActiveErrorRef.current) {
      void segmentClient.logScreenEvent('Active Barcode Error', {
        name: 'Active Barcode Error',
        category: 'deposit_cash',
        subcategory: 'cash_deposit_barcode',
        flow_name: flowName,
      });
      hasLoggedActiveErrorRef.current = true;
    }
  }, [hideRetryButton, segmentClient, flowName]);

  const handleRetryAnalytics = usePressableAnalytics({
    analyticsId: 'retry',
    customAttributes: {
      category: 'deposit_cash',
      source_name: 'cash_deposit_location_map',
      subcategory: 'cash_deposit_barcode',
      flow_name: flowName,
    },
  });

  const handleBackAnalytics = usePressableAnalytics({
    analyticsId: 'back',
    customAttributes: {
      category: 'deposit_cash',
      source_name: 'cash_deposit_location_map',
      subcategory: 'cash_deposit_barcode',
      flow_name: flowName,
    },
  });

  const handleRetry = () => {
    handleRetryAnalytics();

    if (onRetry) {
      onRetry();
    } else if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(authenticated)/(tabs)');
    }
  };

  const handleGoBack = () => {
    if (hideRetryButton) {
      void segmentClient.logEvent('Back clicked', {
        category: 'deposit_cash',
        source_name: 'cash_deposit_location_map',
        subcategory: 'cash_deposit_barcode',
        flow_name: flowName,
      });
    } else {
      handleBackAnalytics();
    }

    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(authenticated)/(tabs)');
    }
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline={
          messageHeadline ??
          translatedText('depositCash.barcode.barcodeError.title')
        }
        messageBody={
          messageBody ??
          translatedText('depositCash.barcode.barcodeError.subtitle')
        }
        primaryButton={
          hideRetryButton
            ? {
                title: translatedText(
                  'depositCash.barcode.barcodeError.backButton',
                ),
                onPress: handleGoBack,
              }
            : {
                title: translatedText(
                  'depositCash.barcode.barcodeError.retryButton',
                ),
                onPress: handleRetry,
              }
        }
        secondaryButton={
          hideRetryButton
            ? undefined
            : {
                title: translatedText(
                  'depositCash.barcode.barcodeError.backButton',
                ),
                onPress: handleGoBack,
              }
        }
        insetBottomSafeArea={true}
      />
    </>
  );
}
